<script setup>
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps({
  tabs: { type: Array, required: true },
  index: { type: Number, default: 0 },
  storedOrder: { type: Array, default: () => [] },
});
const emit = defineEmits(['change', 'order-change']);
const savedOrder = ref([]);
const draggedKey = ref(null);
const dragStartOrder = ref([]);
const dropCommitted = ref(false);
let suppressClickUntil = 0;

const orderedTabs = computed(() => {
  const rank = new Map(savedOrder.value.map((key, index) => [key, index]));
  return [...props.tabs].sort(
    (left, right) =>
      (rank.get(left.key) ?? Number.MAX_SAFE_INTEGER) -
      (rank.get(right.key) ?? Number.MAX_SAFE_INTEGER)
  );
});

watch(
  () => props.storedOrder,
  order => {
    draggedKey.value = null;
    dragStartOrder.value = [];
    dropCommitted.value = false;
    savedOrder.value = Array.isArray(order)
      ? [...new Set(order.filter(item => typeof item === 'string'))]
      : [];
  },
  { immediate: true, deep: true }
);

function moveTab(key, targetKey, persist = true) {
  const keys = orderedTabs.value.map(tab => tab.key);
  const from = keys.indexOf(key);
  const to = keys.indexOf(targetKey);
  if (from < 0 || to < 0 || from === to) return;
  keys.splice(from, 1);
  keys.splice(to, 0, key);
  savedOrder.value = keys;
  if (!persist) return;
  persistOrder();
}

function persistOrder() {
  emit('order-change', [...savedOrder.value]);
}

function startDrag(event, key) {
  dragStartOrder.value = orderedTabs.value.map(tab => tab.key);
  dropCommitted.value = false;
  draggedKey.value = key;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', key);
}

function finishDrag() {
  if (!dropCommitted.value && dragStartOrder.value.length) {
    savedOrder.value = dragStartOrder.value;
  }
  draggedKey.value = null;
  dragStartOrder.value = [];
  dropCommitted.value = false;
  suppressClickUntil = Date.now() + 250;
}

function allowDrop(event) {
  if (!draggedKey.value) return;
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
}

function previewTabPosition(event, key) {
  allowDrop(event);
  if (draggedKey.value && draggedKey.value !== key) {
    moveTab(draggedKey.value, key, false);
  }
}

function dropTab(event) {
  if (!draggedKey.value) return;
  event.preventDefault();
  dropCommitted.value = true;
  persistOrder();
  finishDrag();
}

function guardDragClick(event) {
  if (draggedKey.value || Date.now() < suppressClickUntil) {
    event.preventDefault();
    event.stopPropagation();
  }
}

function onTabKeydown(event, tab) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    emit('change', tab.index);
  } else if (event.altKey && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
    event.preventDefault();
    const tabElement = event.currentTarget;
    const position = orderedTabs.value.findIndex(item => item.key === tab.key);
    const target = orderedTabs.value[
      position + (event.key === 'ArrowLeft' ? -1 : 1)
    ];
    if (target) {
      moveTab(tab.key, target.key);
      nextTick(() => tabElement.focus());
    }
  }
}
</script>

<template>
  <woot-tabs
    :index="index"
    class="-mt-px border-t border-t-n-background"
    @change="emit('change', $event)"
    @click.capture="guardDragClick"
  >
    <woot-tabs-item
      v-for="tab in orderedTabs"
      :key="tab.key"
      :index="tab.index"
      :name="tab.name"
      :show-badge="false"
      draggable="true"
      tabindex="0"
      is-compact
      class="[&_a]:pt-1 [&_a]:cursor-default rounded-sm transition-all duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
      :class="{
        'opacity-50 scale-[0.98] [&_a]:!cursor-grabbing':
          draggedKey === tab.key,
      }"
      @dragstart.stop="startDrag($event, tab.key)"
      @dragenter.stop="previewTabPosition($event, tab.key)"
      @dragover.stop="allowDrop"
      @drop.stop="dropTab"
      @dragend="finishDrag"
      @keydown="onTabKeydown($event, tab)"
    />
  </woot-tabs>
</template>
